import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';

import { Lesson } from '@df/interfaces';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { E_SYM_KEY_CODE, MINUS_SYM_KEY_CODE, MINUTES_IN_ONE_DAY, PLUS_SYM_KEY_CODE } from '@app/app.constants';

@Component({
    selector: 'app-time-correction-modal',
    templateUrl: './time-correction-modal.component.html',
    styleUrls: ['./time-correction-modal.component.scss']
})
export class TimeCorrectionModalComponent implements OnInit {
    @ViewChild('myModal', { static: true }) myModal: TemplateRef<any>;
    @Output() lessonForUpdate = new EventEmitter<{ correctionData: any; hasTimeCorrectionChanges: boolean }>();
    @Input() lesson: Lesson;
    @Input() lessonCompletedNotFinal: number;
    public textButtonDecline = 'ОТМЕНА';
    public textButtonTextConfirm = 'ЗАВЕРШИТЬ';
    public textButtonTextSave = 'СОХРАНИТЬ';
    public buttonModifier = 'button_mode_cancel';
    public isTimeCorrectionFormOpen = false;
    public durationTime = 0;
    public timeCorrectionForm: FormGroup;
    private timeCorrectionData: { startTime: Date; endTime: Date; date: Date; duration: number };

    private modalRef: BsModalRef;

    static validateTimeCorrectionForm(formGroup: FormGroup): object {
        if (formGroup.invalid) {
            return {
                invalidFormData: true
            };
        }
        const lessonDate = formGroup.get('lessonDate').value;
        const startTimeHour = formGroup.get('startTimeHour').value;
        const startTimeMinute = formGroup.get('startTimeMinute').value;
        const endTimeHour = formGroup.get('endTimeHour').value;
        const endTimeMinute = formGroup.get('endTimeMinute').value;

        const startTime = new Date(lessonDate);
        startTime.setHours(startTimeHour);
        startTime.setMinutes(startTimeMinute);

        const endTime = new Date(lessonDate);
        endTime.setHours(endTimeHour);
        endTime.setMinutes(endTimeMinute);

        const startTimeInMS = startTime.getTime();
        const endTimeInMS = endTime.getTime();
        const error = { invalidEndTime: true };
        if (startTimeInMS >= endTimeInMS) {
            formGroup.get('endTimeHour').setErrors(error);
            formGroup.get('endTimeMinute').setErrors(error);
            return error;
        }
        error['invalidEndTime'] = null;
        formGroup.get('endTimeHour').setErrors(error);
        formGroup.get('endTimeMinute').setErrors(error);
        formGroup.get('endTimeHour').updateValueAndValidity({ onlySelf: true });
        formGroup.get('endTimeMinute').updateValueAndValidity({ onlySelf: true });
        return null;
    }

    constructor(
        private modalService: BsModalService,
        private datePipe: DatePipe,
        private correctionFormBuilder: FormBuilder
    ) {
        // Функция позволяет заполнить валидатор для времени нужными валидаторами
        const generateTimeValidator = (isHourValidator = false) => {
            return [
                Validators.required,
                Validators.min(0),
                Validators.max(isHourValidator ? 23 : 59),
                Validators.maxLength(2)
            ];
        };

        this.timeCorrectionForm = this.correctionFormBuilder.group(
            {
                lessonDate: new FormControl(null, Validators.required),
                startTimeHour: new FormControl(null, Validators.compose(generateTimeValidator(true))),
                startTimeMinute: new FormControl(null, Validators.compose(generateTimeValidator())),
                endTimeHour: new FormControl(null, Validators.compose(generateTimeValidator(true))),
                endTimeMinute: new FormControl(null, Validators.compose(generateTimeValidator()))
            },
            {
                validator: TimeCorrectionModalComponent.validateTimeCorrectionForm
            }
        );
    }

    ngOnInit(): void {}

    private fillTimeCorrectionForm(): void {
        // Проверяем, если нет времени окончания то используем текущее время.
        let lessonEndTime = null;
        if (this.lesson.end_time === null) {
            lessonEndTime = new Date();
        } else {
            lessonEndTime = new Date(this.lesson.end_time);
        }

        const lessonDate = this.datePipe.transform(new Date(this.lesson.start_time), 'y-MM-dd');
        const lessonStartTimeHour = this.datePipe.transform(new Date(this.lesson.start_time), 'HH');
        const lessonStartTimeMinute = this.datePipe.transform(new Date(this.lesson.start_time), 'mm');
        const lessonEndTimeHour = this.datePipe.transform(lessonEndTime, 'HH');
        const lessonEndTimeMinute = this.datePipe.transform(lessonEndTime, 'mm');

        // Заполняем форму полученными данными
        this.timeCorrectionForm.setValue({
            lessonDate: lessonDate,
            startTimeHour: lessonStartTimeHour,
            startTimeMinute: lessonStartTimeMinute,
            endTimeHour: lessonEndTimeHour,
            endTimeMinute: lessonEndTimeMinute
        });
        this.refreshDuration();
    }

    public showModal(): void {
        // вызывает модалку для подтверждения закрытия урока
        this.fillTimeCorrectionForm();
        this.modalRef = this.modalService.show(this.myModal, { class: 'modal-dialog-custom' });
    }

    private confirmChanges(): void {
        // когда пользователь подтвердил закрытие урока, то вызывается
        // данный метод для того чтобы изменить статус урока, сначало он
        // закрывает модальное  окно, а затем вызывает метод родителя который
        // меняет состояние урока, параметром передаём то состояние на которое
        // необходимо поменять
        this.modalRef.hide();
        const lessonForUpdate = {
            correctionData: this.timeCorrectionData,
            hasTimeCorrectionChanges: this.isTimeCorrectionFormOpen
        };
        this.lessonForUpdate.emit(lessonForUpdate);
    }

    checkTimeInput($event) {
        const keyCode = $event.which;
        // Не разрешаем ввод символов таких как +, - и e
        if (keyCode === PLUS_SYM_KEY_CODE || keyCode === MINUS_SYM_KEY_CODE || keyCode === E_SYM_KEY_CODE) {
            return false;
        }
    }

    private refreshDuration(): void {
        const lessonDate = new Date(this.timeCorrectionForm.get('lessonDate').value);
        const startHour = this.timeCorrectionForm.get('startTimeHour').value;
        const startMinute = this.timeCorrectionForm.get('startTimeMinute').value;
        const endHour = this.timeCorrectionForm.get('endTimeHour').value;
        const endMinute = this.timeCorrectionForm.get('endTimeMinute').value;

        const startTime = new Date(lessonDate);
        startTime.setHours(startHour);
        startTime.setMinutes(startMinute);

        const endTime = new Date(lessonDate);
        endTime.setHours(endHour);
        endTime.setMinutes(endMinute);

        let lessonDuration = endTime.getTime() - startTime.getTime();
        if (lessonDuration < 0) {
            lessonDuration = 0;
        }
        lessonDuration = Math.round(lessonDuration / 1000 / 60);

        if (lessonDuration > MINUTES_IN_ONE_DAY) {
            lessonDuration = MINUTES_IN_ONE_DAY;
        }

        this.durationTime = lessonDuration;
    }

    public submitTime(): void {
        if (this.isTimeCorrectionFormOpen) {
            const lessonDate = this.timeCorrectionForm.get('lessonDate').value;
            const startTimeHour = this.timeCorrectionForm.get('startTimeHour').value;
            const startTimeMinute = this.timeCorrectionForm.get('startTimeMinute').value;
            const endTimeHour = this.timeCorrectionForm.get('endTimeHour').value;
            const endTimeMinute = this.timeCorrectionForm.get('endTimeMinute').value;

            const startTime = new Date(lessonDate);
            startTime.setHours(startTimeHour);
            startTime.setMinutes(startTimeMinute);

            const endTime = new Date(lessonDate);
            endTime.setHours(endTimeHour);
            endTime.setMinutes(endTimeMinute);

            this.timeCorrectionData = {
                date: new Date(lessonDate),
                startTime: startTime,
                endTime: endTime,
                duration: this.durationTime
            };
            this.lesson.status = this.lessonCompletedNotFinal;
        }

        this.lesson.status = this.lessonCompletedNotFinal;
        this.confirmChanges();
    }

    public decline(): void {
        this.isTimeCorrectionFormOpen = false;
        this.timeCorrectionForm.reset();
        this.modalRef.hide();
    }
}
